/**
 * How a PERSON is labelled — the one ordering rule, shared by the app's UI and
 * the API's outbound email.
 *
 * The rule itself was settled in `apps/app`'s `UserPreview.tsx` (VERA-304) after
 * two call sites drifted into showing different things for the same person. This
 * module is that rule's dependency-free home, so the API can obey it too rather
 * than growing a third name-vs-username ternary (working-conventions §36).
 *
 * ORDER, and why:
 *   1. Real name  — what a person recognises.
 *   2. `@handle`  — the Venmo/PayPal model. Becomes the PRIMARY line when there
 *                   is no name, and the subtext is dropped, because "spencersedb"
 *                   over "@spencersedb" is the same string twice.
 *   3. Email      — UI ONLY, and only as a last resort. See the warning below.
 *
 * ⚠️ THE EMAIL STEP DOES NOT TRANSFER TO EMAIL COPY. In the app it is defensible
 * because it appears in the invite-by-email case, where the viewer has just typed
 * that address themselves and is looking at their own screen. Neither condition
 * holds in an outbound message: the reader did not type it, and the address
 * belongs to somebody else. Two separate failures follow from copying the step
 * across —
 *
 *   - it reads as a bug. "spencer+new@vera.inc has gifted you…" is the same class
 *     of defect as the "Welcome, spencer+new!" greeting this epic already removed,
 *     where a machine-derived string was presented as a person's name.
 *   - it discloses the sender's address to the recipient, which nothing in the
 *     gift flow asks the sender to consent to.
 *
 * So outbound copy uses `resolvePersonName`, which stops at the handle and
 * returns null when it can name nobody. The caller then has to write a sentence
 * that works without a person, instead of inventing one from an address.
 */

export interface UserIdentitySource {
  first_name?: string | null;
  last_name?: string | null;
  username?: string | null;
  email?: string | null;
}

/**
 * Real name, else `@handle`, else NULL. Never an email address.
 *
 * The primitive both consumers share. Trims before testing truthiness, because
 * `first_name` is nullable free text and a form that submitted a blank field
 * stores `'   '` — which is not a name and must take the same branch as null.
 */
export const resolvePersonName = (
  user?: UserIdentitySource | null,
): string | null => {
  if (!user) return null;

  const fullName = [user.first_name, user.last_name]
    .map(part => part?.trim())
    .filter(Boolean)
    .join(' ')
    .trim();
  if (fullName) return fullName;

  const handle = user.username?.trim();
  if (handle) return `@${handle}`;

  return null;
};

/**
 * The UI resolver: `resolvePersonName` plus the email last-resort and the
 * em-dash placeholder that surfaces expect.
 *
 * `apps/app` still carries its own copy in `components/UI/UserPreview.tsx`. That
 * copy should import this one instead — left un-migrated only because a
 * concurrent session owns that file; the behaviour here is identical to it.
 */
export const resolveUserIdentity = (
  user?: UserIdentitySource | null,
): {name: string; subtext: string | null} => {
  if (!user) return {name: '—', subtext: null};

  const resolved = resolvePersonName(user);
  const handle = user.username?.trim() ? `@${user.username.trim()}` : null;

  // A resolved value that is NOT the handle means a real name was found, so the
  // handle stays available as the secondary line.
  if (resolved && resolved !== handle) return {name: resolved, subtext: handle};
  if (resolved) return {name: resolved, subtext: null};

  return {name: user.email || '—', subtext: null};
};
